import { randomId } from './id';
import type { Coords, SavedRide } from './types';

/**
 * Wiederkehrende Fahrten: Startpunkt, Abholpunkt und - wenn vorhanden - der
 * Kontakt, mit dem sie gefahren wird. "Freitag zum Bahnhof" ist damit zwei
 * Tipps statt jedes Mal zwei Punkte auf der Karte.
 *
 * Wie die gespeicherten Orte bleibt das bewusst **lokal**: das sind
 * Heimatadressen und Gewohnheiten, die haben auf einem Hobby-Server nichts
 * verloren. Folge: sie wandern nicht aufs nächste Gerät.
 */

const KEY = 'r2f.rides';
const MAX_RIDES = 20;

function read(): SavedRide[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedRide[]) : [];
  } catch {
    return [];
  }
}

function write(rides: SavedRide[]): SavedRide[] {
  const trimmed = rides.slice(0, MAX_RIDES);
  try {
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    // Kein Speicher - die Liste lebt dann nur für diese Sitzung.
  }
  return trimmed;
}

/** Zuletzt benutzte zuerst. */
export function listSavedRides(): SavedRide[] {
  return read().sort((a, b) => b.usedAt - a.usedAt);
}

export function saveRide(input: {
  name: string;
  origin: Coords;
  pickup?: Coords | null;
  contactId?: string | null;
  contactLabel?: string | null;
}): SavedRide[] {
  const now = Date.now();
  const ride: SavedRide = {
    id: randomId(),
    name: input.name.trim().slice(0, 40) || 'Fahrt ohne Namen',
    origin: input.origin,
    pickup: input.pickup ?? null,
    contactId: input.contactId ?? null,
    contactLabel: input.contactLabel ?? null,
    createdAt: now,
    usedAt: now,
  };
  return write([ride, ...read()]);
}

export function deleteSavedRide(id: string): SavedRide[] {
  return write(read().filter((ride) => ride.id !== id));
}

/** Nach oben schieben, damit die Schnellauswahl der Gewohnheit folgt. */
export function touchSavedRide(id: string): SavedRide[] {
  return write(read().map((ride) => (ride.id === id ? { ...ride, usedAt: Date.now() } : ride)));
}

export function renameSavedRide(id: string, name: string): SavedRide[] {
  const clean = name.trim().slice(0, 40);
  if (!clean) return listSavedRides();
  return write(read().map((ride) => (ride.id === id ? { ...ride, name: clean } : ride)));
}
